import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Camera, Video, Mic, MicOff, SwitchCamera, Check, AlertTriangle, ShieldCheck, MapPin, Aperture, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import { analyzeImageQuality } from "@/lib/imageQuality";
import { compressImage } from "@/lib/image";
import { listCameras, videoConstraints } from "@/lib/camera";
import { toast } from "sonner";

// Full-screen "walk, talk & shoot" capture. Photo mode captures room-tagged
// evidence with the commentary spoken around each shot (video mode = next phase).
export default function WalkthroughCapture({ open, onClose, inspectionId, sections, piiScan, online, onCaptured, onAssign, patchMedia, guided = false, startRoom = 0, onRoomChange }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const micOnRef = useRef(false);
  const segsRef = useRef([]); // {t, text}
  const lastShotRef = useRef(Date.now()); // commentary is linked per-photo: only words spoken since the last shot
  const audioStreamRef = useRef(null);
  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [facing, setFacing] = useState("environment");
  const [cameras, setCameras] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [mode, setMode] = useState("photo");
  const [room, setRoom] = useState(startRoom);
  const [step, setStep] = useState("overview"); // guided: 'overview' | 'details'
  const [matchedByRoom, setMatchedByRoom] = useState({}); // room -> Set(item_key)
  const [askSkip, setAskSkip] = useState(false); // finish-guard confirm
  const [visited, setVisited] = useState(new Set());
  const [overallRooms, setOverallRooms] = useState(new Set());
  const [overviewCounts, setOverviewCounts] = useState({});
  const [overallMode, setOverallMode] = useState(true);
  const [count, setCount] = useState(0);
  const [last, setLast] = useState(null);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState("");
  const [micOn, setMicOn] = useState(false);
  const [live, setLive] = useState("");
  const [busy, setBusy] = useState(false);
  const supportsSR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  // camera stream
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints(deviceId, facing), audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        const cams = await listCameras();
        if (!cancelled) setCameras(cams);
      } catch (e) { setError("Camera unavailable. Check permissions and use HTTPS."); }
    })();
    return () => { cancelled = true; streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  }, [open, facing, deviceId]);

  const cycleLens = () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.deviceId === deviceId);
    setDeviceId(cameras[(idx + 1) % cameras.length].deviceId);
  };
  const lensLabel = cameras.find((c) => c.deviceId === deviceId)?.label;

  const audioMime = () => {
    if (typeof MediaRecorder === "undefined") return "";
    for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]) {
      if (MediaRecorder.isTypeSupported?.(m)) return m;
    }
    return "";
  };
  const startMediaRec = () => {
    const stream = audioStreamRef.current;
    if (!stream || typeof MediaRecorder === "undefined") return;
    try {
      const mime = audioMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) audioChunksRef.current.push(e.data); };
      mediaRecRef.current = mr;
      mr.start();
    } catch (e) {}
  };
  // Stop the current clip (words since the last shutter), return it, and restart for the next shot.
  const cutAudioClip = () =>
    new Promise((resolve) => {
      const mr = mediaRecRef.current;
      if (!mr || mr.state === "inactive") { resolve(null); return; }
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        audioChunksRef.current = [];
        startMediaRec();
        resolve(blob.size > 1200 ? blob : null); // ignore near-silent clips
      };
      try { mr.stop(); } catch (e) { resolve(null); }
    });
  const blobToDataUrl = (blob) =>
    new Promise((resolve) => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.readAsDataURL(blob); });

  const startRec = async () => {
    // Audio recording (for server-side Whisper) works even where the browser Web Speech API doesn't.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      startMediaRec();
    } catch (e) {}
    micOnRef.current = true; setMicOn(true); lastShotRef.current = Date.now();
    if (supportsSR) {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const rec = new SR();
      rec.lang = "en-AU"; rec.continuous = true; rec.interimResults = true;
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) segsRef.current.push({ t: Date.now(), text: t.trim() });
          else interim += t;
        }
        setLive(interim);
      };
      rec.onend = () => { if (micOnRef.current) { try { rec.start(); } catch (e) {} } };
      rec.onerror = () => {};
      recRef.current = rec;
      try { rec.start(); } catch (e) {}
    }
  };
  const stopRec = () => {
    micOnRef.current = false; setMicOn(false);
    try { recRef.current?.stop(); } catch (e) {}
    try { if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") mediaRecRef.current.stop(); } catch (e) {}
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current = null; mediaRecRef.current = null;
    setLive("");
  };
  useEffect(() => () => stopRec(), []);

  // Default the next shot to "overall" whenever we enter a room that has no wide shot yet.
  const roomTitle = sections[room]?.title;
  const roomHasOverall = overallRooms.has(roomTitle);
  const roomItems = sections[room]?.items || [];
  const matchedCount = matchedByRoom[roomTitle]?.size || 0;
  // When entering a room, set the guided step (wide shot first unless the room already has one)
  // and default the overall-shot tagging accordingly.
  useEffect(() => {
    const has = overallRooms.has(sections[room]?.title);
    if (guided) setStep(has ? "details" : "overview");
    setOverallMode(!has);
    setAskSkip(false);
    onRoomChange?.(room);
  }, [room]);  // eslint-disable-line

  const goToRoom = (i) => { if (i >= 0 && i < sections.length) setRoom(i); };
  const doNext = () => { setAskSkip(false); if (room < sections.length - 1) setRoom(room + 1); else onClose(); };
  const nextRoom = () => {
    // Finish guard: don't let a room be left without a validated wide shot without confirming.
    if (guided && !overallRooms.has(sections[room]?.title) && !askSkip) { setAskSkip(true); return; }
    doNext();
  };

  const recentCommentary = () => {
    // Only the commentary spoken since the previous shot — so each photo links to the
    // right words ("here's the shower" vs "here's the bath, bit of mould").
    const since = lastShotRef.current;
    const recent = segsRef.current.filter((s) => s.t >= since).map((s) => s.text);
    return [...recent, live].filter(Boolean).join(" ").trim();
  };

  const shoot = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || busy) return;
    setBusy(true);
    setFlash(true); setTimeout(() => setFlash(false), 160);
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = await compressImage(c.toDataURL("image/jpeg", 0.9));
    const segment = recentCommentary();
    let audioDataUrl = null;
    if (micOnRef.current) { const blob = await cutAudioClip(); if (blob) audioDataUrl = await blobToDataUrl(blob); }
    lastShotRef.current = Date.now(); // start a fresh commentary window for the next shot
    const roomTitle = sections[room].title;
    const isOverall = guided ? step === "overview" : overallMode;
    try {
      const q = await analyzeImageQuality(dataUrl);
      if (!q.ok) {
        const issues = [q.tooDark && "low light", q.blurry && "blurry"].filter(Boolean).join(", ");
        if (issues) toast.warning(`Shot may be ${issues}`, { duration: 1500 });
      }
      const { data } = await api.post("/media", {
        inspection_id: inspectionId, data_url: dataUrl, room: roomTitle, source: "walkthrough",
        captured_at: new Date().toISOString(), transcript_segment: segment, audio_data_url: audioDataUrl, scan_pii: piiScan,
        shot_type: isOverall ? "overall" : "detail",
      });
      setCount((n) => n + 1);
      setVisited((s) => new Set(s).add(roomTitle));
      setLast(dataUrl);
      const online2 = online && navigator.onLine !== false;
      if (isOverall && !online2) {
        // Offline: optimistically accept the wide shot; batch validation runs on close.
        setOverallRooms((s) => new Set(s).add(roomTitle));
        setOverviewCounts((c) => ({ ...c, [roomTitle]: (c[roomTitle] || 0) + 1 }));
        if (guided) setStep("details");
      }
      onCaptured?.({ ...data, room: roomTitle, item_key: null, data_url: dataUrl, shot_type: isOverall ? "overall" : "detail" });
      if (piiScan && data.pii?.redacted) toast.success("Blurred sensitive content", { duration: 1200 });
      // Hybrid: classify in real-time when online; offline shots stay pooled for batch.
      // Overview shots are accepted as room context (no framing validation); details are item-matched.
      if (online2) {
        api.post(`/media/${data.id}/classify`).then(({ data: c }) => {
          if (isOverall) {
            setOverallRooms((s) => new Set(s).add(roomTitle));
            setOverviewCounts((cc) => ({ ...cc, [roomTitle]: (cc[roomTitle] || 0) + 1 }));
            patchMedia?.(data.id, { scene_type: c.scene_type, overview_valid: true });
            if (guided) { setStep("details"); toast.success(`Wide shot ✓ — now capture each item in ${roomTitle}`, { duration: 2000 }); }
            else toast.success(`Wide shot of ${roomTitle} captured`, { duration: 1200 });
            return;
          }
          if (c.item_key) {
            const byVoice = c.assigned_by === "voice";
            patchMedia?.(data.id, { item_key: c.item_key, auto_assigned: true, assigned_by: c.assigned_by, ai_item_confidence: c.confidence, ai_item_reason: c.reason, scene_type: c.scene_type });
            onAssign?.(c.item_key, roomTitle, c.analysis, byVoice);
            setMatchedByRoom((prev) => ({ ...prev, [roomTitle]: new Set([...(prev[roomTitle] || []), c.item_key]) }));
            // Match shown silently via the item badge + live progress chips — no per-shot toast.
          } else if (c.ai_candidate) {
            patchMedia?.(data.id, { ai_candidate: c.ai_candidate, ai_analysis: c.analysis, scene_type: c.scene_type });
            toast.message(`AI thinks: ${c.ai_candidate.label} — confirm in the room list`, { duration: 2000 });
          }
        }).catch(() => {});
      }
    } catch (e) { toast.error("Capture failed"); }
    setBusy(false);
  };

  if (!open) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black" data-testid="walkthrough-modal">
      {/* Full-screen viewfinder */}
      {error ? (
        <div className="absolute inset-0 grid place-items-center text-center p-8 text-white/80 text-sm">{error}</div>
      ) : (
        <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-cover" data-testid="walkthrough-video" />
      )}
      {flash && <div className="absolute inset-0 bg-white" />}

      {/* top bar */}
      <div className="absolute top-0 inset-x-0 p-4 pt-6 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
        <div className="inline-flex p-1 rounded-full bg-black/40 backdrop-blur">
          {[["photo", Camera, "Photo"], ["video", Video, "Video"]].map(([m, Icon, label]) => (
            <button key={m} data-testid={`walk-mode-${m}`} onClick={() => setMode(m)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
              style={{ background: mode === m ? "#fff" : "transparent", color: mode === m ? "#000" : "#fff" }}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
        <button data-testid="walk-close" onClick={onClose} className="h-9 w-9 rounded-full grid place-items-center bg-black/40 text-white backdrop-blur"><X size={18} /></button>
      </div>

      {/* HUD counts */}
      <div className="absolute top-20 left-4 flex flex-col gap-1.5">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-black/45 text-white backdrop-blur" data-testid="walk-count"><Camera size={12} /> {count} photos</span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-black/45 text-white backdrop-blur"><MapPin size={12} /> {visited.size}/{sections.length} areas</span>
        {piiScan && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-black/45 text-white backdrop-blur"><ShieldCheck size={12} /> Auto-redact</span>}
      </div>

      {cameras.length > 1 && mode === "photo" && (
        <button data-testid="walk-lens" onClick={cycleLens}
          className="absolute top-20 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-black/45 text-white backdrop-blur active:scale-95 transition-transform">
          <Aperture size={13} /> {lensLabel || "Lens"}
        </button>
      )}

      {mode === "video" && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center px-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-black/55 text-white backdrop-blur">
              <Video size={13} /> Video walkthrough — coming in the next phase
            </div>
            <p className="text-white/70 text-xs mt-2 max-w-xs mx-auto">Continuous video with keyframe extraction is on the roadmap. Use Photo mode for evidence capture today.</p>
          </div>
        </div>
      )}

      {/* Bottom overlay: commentary + rooms + controls + done */}
      <div className="absolute bottom-0 inset-x-0 pt-16 pb-safe bg-gradient-to-t from-black/85 via-black/55 to-transparent">
        {micOn && (
          <div className="px-4 mb-2">
            <div className="rounded-xl px-3 py-2 bg-black/50 text-white text-xs backdrop-blur flex items-start gap-2" data-testid="walk-live-transcript">
              <Mic size={13} className="mt-0.5 shrink-0 text-red-400 animate-pulse" />
              <span className="line-clamp-2">{live || "Listening… narrate what you see."}</span>
            </div>
          </div>
        )}

        {/* Guided step panel (in-viewfinder step-by-step) */}
        {mode === "photo" && guided && (
          <div className="px-4 pb-2" data-testid="walk-guided-panel">
            <div className="flex items-center gap-2 text-white text-xs font-semibold mb-1.5">
              <span className="px-2 py-0.5 rounded-full bg-white/20" data-testid="walk-guided-roomidx">Room {room + 1}/{sections.length}</span>
              <span className="truncate">{roomTitle}</span>
            </div>
            {step === "overview" ? (
              <div className="rounded-xl px-3 py-2 bg-black/50 text-white text-xs backdrop-blur flex items-center gap-2" data-testid="walk-guided-step-overview">
                <Aperture size={14} className="shrink-0" />
                <span><b>Step 1 of 2 — Wide shot.</b> Frame the whole {roomTitle} from a doorway or corner, then tap the shutter.</span>
              </div>
            ) : (
              <div data-testid="walk-guided-step-details">
                <div className="rounded-xl px-3 py-2 bg-black/50 text-white text-xs backdrop-blur flex items-center justify-between gap-2 mb-1.5">
                  <span className="flex items-center gap-2 min-w-0"><Camera size={14} className="shrink-0" /><span className="truncate"><b>Step 2 of 2 — Items.</b> {matchedCount}/{roomItems.length} documented</span></span>
                  <button data-testid="walk-guided-add-wide" onClick={() => setStep("overview")} className="shrink-0 text-[11px] underline text-white/70">+ wide shot</button>
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-0.5" data-testid="walk-guided-items">
                  {roomItems.map((it) => {
                    const done = matchedByRoom[roomTitle]?.has(it.key);
                    return (
                      <span key={it.key} className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ background: done ? "var(--success)" : "rgba(255,255,255,0.16)", color: "#fff" }}>
                        {done && <Check size={9} />}{it.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* overall-shot coach + toggle (free-form / advanced) */}
        {mode === "photo" && !guided && (
          <div className="px-4 pb-2 flex items-center justify-between gap-3">
            {!roomHasOverall ? (
              <span className="text-[11px] text-white/85 flex items-center gap-1.5" data-testid="walk-overall-hint">
                <Aperture size={12} /> Start with a wide shot of {roomTitle} — try the ultra-wide lens
              </span>
            ) : (
              <span className="text-[11px] text-white/55 flex items-center gap-1.5" data-testid="walk-overall-done">
                <Check size={12} /> {roomTitle}: {overviewCounts[roomTitle] || 1} wide shot{(overviewCounts[roomTitle] || 1) > 1 ? "s" : ""} · add more angles if needed
              </span>
            )}
            <button data-testid="walk-overall-toggle" onClick={() => setOverallMode((v) => !v)}
              className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors"
              style={{ background: overallMode ? "#fff" : "rgba(255,255,255,0.18)", color: overallMode ? "#000" : "#fff" }}>
              <Aperture size={12} /> Overall shot {overallMode ? "on" : "off"}
            </button>
          </div>
        )}

        {/* room chips */}
        <div className="flex gap-1.5 overflow-x-auto px-4 pb-3" data-testid="walk-rooms">
          {sections.map((s, i) => (
            <button key={s.key} data-testid={`walk-room-${i}`} onClick={() => setRoom(i)}
              className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
              style={{ background: room === i ? "#fff" : "rgba(255,255,255,0.16)", color: room === i ? "#000" : "#fff" }}>
              {s.title}{visited.has(s.title) ? " ✓" : ""}
            </button>
          ))}
        </div>

        {/* controls */}
        <div className="flex items-center justify-between px-8 pb-3">
          <div className="w-14 h-14">
            {last && <img src={last} alt="last" className="h-14 w-14 rounded-xl object-cover border border-white/30" data-testid="walk-last-thumb" />}
          </div>
          <button data-testid="walk-shutter" onClick={shoot} disabled={!!error || busy || mode !== "photo"}
            className="rounded-full grid place-items-center disabled:opacity-30" style={{ height: 76, width: 76, background: "rgba(255,255,255,0.2)" }}>
            <div className="h-16 w-16 rounded-full bg-white active:scale-90 transition-transform" />
          </button>
          <div className="w-14 flex flex-col items-center gap-3">
            <button data-testid="walk-flip" onClick={() => { setDeviceId(null); setFacing((f) => (f === "environment" ? "user" : "environment")); }} className="text-white/90"><SwitchCamera size={22} /></button>
            {(supportsSR || typeof MediaRecorder !== "undefined") && (
              <button data-testid="walk-mic" onClick={() => (micOn ? stopRec() : startRec())}
                className="h-9 w-9 rounded-full grid place-items-center" style={{ background: micOn ? "#ef4444" : "rgba(255,255,255,0.2)", color: "#fff" }}>
                {micOn ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
            )}
          </div>
        </div>

        {/* Finish guard */}
        {askSkip && (
          <div className="px-4 pb-2" data-testid="walk-finish-guard">
            <div className="rounded-xl px-3 py-2.5 border backdrop-blur" style={{ background: "rgba(0,0,0,0.6)", borderColor: "var(--warning)" }}>
              <div className="flex items-center gap-2 text-white text-xs font-semibold">
                <AlertTriangle size={14} style={{ color: "var(--warning)" }} />
                No wide shot of {roomTitle} yet — leaving without one weakens the report.
              </div>
              <div className="flex gap-2 mt-2">
                <button data-testid="walk-guard-capture" onClick={() => { setAskSkip(false); setStep("overview"); }}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold" style={{ background: "#fff", color: "#000" }}>
                  Capture wide shot
                </button>
                <button data-testid="walk-guard-skip" onClick={doNext}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold border text-white/80" style={{ borderColor: "rgba(255,255,255,0.3)" }}>
                  {room < sections.length - 1 ? "Skip room" : "Finish anyway"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* done / next */}
        <div className="px-6 pb-5">
          {guided ? (
            <button data-testid="walk-next-room" onClick={nextRoom}
              className="w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2" style={{ background: "#fff", color: "#000" }}>
              {room < sections.length - 1
                ? <><ArrowRight size={17} /> Next room ({room + 2}/{sections.length})</>
                : <><Check size={17} /> Finish & review ({count} captured)</>}
            </button>
          ) : (
            <button data-testid="walk-done" onClick={onClose}
              className="w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2" style={{ background: "#fff", color: "#000" }}>
              <Check size={17} /> Done — review checklist ({count} captured)
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
